import type {
  InventoryVehicle,
  SchedulingAvailabilityInfo
} from '@carvana/merch-contracts';
import {
  defaultLoadableState,
  LoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import { type Result } from '@carvana/merch-result';
import deepmerge from 'deepmerge';
import { isPlainObject } from 'is-plain-object';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import type { Pagination } from '~/contracts/common';
import type { UserDeliveryInfo } from '~/contracts/srp';
import InventoryConditionalTile from '~/contracts/srp/InventoryConditionalTile';
import type { PricingResponse } from '~/services/common/contracts';
import type { DeepPartial } from '~/types';
import isServerSideRendering from '~/utilities/isServerSideRendering';

function getPageNumberFromUrl(): number {
  if (isServerSideRendering) return 1;

  const pageNumber = parseInt(
    new URL(window.location.href).searchParams.get('page') ?? '1',
    10
  );

  return Number.isNaN(pageNumber) || pageNumber <= 1 ? 1 : pageNumber;
}

export type Inventory = {
  inventory: {
    conditionalTiles?: InventoryConditionalTile[];
    pagination: Partial<Pagination>;
    vehicles: InventoryVehicle[];
  };
  searchRequestId: string;
  userDeliveryInfo?: UserDeliveryInfo;
};

// One entry per loaded page, in load order (unshift for Load Previous, push
// for Load More) - including the landing page, seeded below - so useTiles can
// chunk the accumulated vehicles without inferring any page's size.
export type LoadMoreResultsTileInfo = {
  conditionalTiles: InventoryConditionalTile[];
  isLandingPage?: boolean;
  isLoadPrevious?: boolean;
  pageNumber: number;
  vehicleCount: number;
};

export type SimilarVehicles = {
  vehicles: InventoryVehicle[];
  searchRequestId: string;
};

export type AvailabilityData = {
  soonestPickup: Result<SchedulingAvailabilityInfo>;
  soonestDelivery: Result<SchedulingAvailabilityInfo>;
};

export type ContextType = {
  canLoadMoreResults: boolean;
  fetchMoreInventoryTrigger: number;
  fetchPreviousInventoryTrigger: number;
  fetchSearchTrigger: number;
  inventory: LoadableState<Inventory>;
  inventoryBulkData: LoadableState<Record<number, AvailabilityData>>;
  isLoadingPreviousResults: boolean;
  loadMoreResults: () => void;
  loadMoreResultsTileInfo: LoadableState<LoadMoreResultsTileInfo[]>;
  loadPreviousResults: () => void;
  previousResultsLoadedCount: number;
  previousSearchRequestId: string;
  recordPreviousResultsLoaded: () => void;
  searchAnalyzeTrigger: boolean;
  similarVehicles: SimilarVehicles;
  resetInventoryPricing: () => void;
  setIsLoadingPreviousResults: React.Dispatch<React.SetStateAction<boolean>>;
  setPreviousSearchRequestId: React.Dispatch<React.SetStateAction<string>>;
  setSearchAnalyzeTrigger: React.Dispatch<React.SetStateAction<boolean>>;
  setSimilarVehicles: React.Dispatch<React.SetStateAction<SimilarVehicles>>;
  triggerFetchSearch: () => void;
  updateInventoryPricing: (_data: PricingResponse) => void;
};

// Seeds the landing page's own entry so useTiles never has to infer its size
// by subtracting later pages from the total - see LoadMoreResultsTileInfo.
// Called both at provider construction (server-rendered landing page) and
// whenever a client-side search resolves (useGetInventory).
export function createLandingPageTileInfo(
  inventory: Inventory['inventory']
): LoadMoreResultsTileInfo[] {
  if (!inventory.vehicles.length) return [];

  return [
    {
      conditionalTiles: inventory.conditionalTiles ?? [],
      isLandingPage: true,
      pageNumber: inventory.pagination.currentPage ?? 1,
      vehicleCount: inventory.vehicles.length
    }
  ];
}

export const defaultInventory: Inventory = {
  inventory: {
    conditionalTiles: [],
    pagination: {
      currentPage: getPageNumberFromUrl()
    },
    vehicles: []
  },
  searchRequestId: '',
  userDeliveryInfo: undefined
};

const defaultContext: ContextType = {
  canLoadMoreResults: false,
  fetchMoreInventoryTrigger: 0,
  fetchPreviousInventoryTrigger: 0,
  fetchSearchTrigger: 0,
  inventory: defaultLoadableState<Inventory>(defaultInventory),
  inventoryBulkData: defaultLoadableState<Record<number, AvailabilityData>>({}),
  isLoadingPreviousResults: false,
  loadMoreResults() {},
  loadMoreResultsTileInfo: defaultLoadableState<LoadMoreResultsTileInfo[]>([]),
  loadPreviousResults() {},
  previousResultsLoadedCount: 0,
  previousSearchRequestId: '',
  recordPreviousResultsLoaded() {},
  searchAnalyzeTrigger: false,
  similarVehicles: { searchRequestId: '', vehicles: [] },
  resetInventoryPricing() {},
  setIsLoadingPreviousResults() {},
  setPreviousSearchRequestId() {},
  setSearchAnalyzeTrigger() {},
  setSimilarVehicles() {},
  triggerFetchSearch() {},
  updateInventoryPricing() {}
};

const InventoryContext = createContext<ContextType>(defaultContext);

export type OwnProps = {
  inventoryData?: Inventory;
  similarVehiclesFromServer?: SimilarVehicles;
};

export function InventoryContextProvider({
  children,
  inventoryData,
  similarVehiclesFromServer
}: React.PropsWithChildren<OwnProps>) {
  const loadableInventoryState = useLoadableState<Inventory>(
    inventoryData ?? defaultInventory,
    {
      initializeAsLoaded: Boolean(
        inventoryData?.inventory?.vehicles?.length ||
          similarVehiclesFromServer?.vehicles?.length
      )
    }
  );
  const [fetchSearchTrigger, setTrigger] = useState<number>(
    defaultContext.fetchSearchTrigger
  );
  const [similarVehicles, setSimilarVehicles] = useState<SimilarVehicles>(
    similarVehiclesFromServer ?? defaultContext.similarVehicles
  );
  const inventoryBulkData = useLoadableState<Record<number, AvailabilityData>>(
    {}
  );
  const loadMoreResultsTileInfo = useLoadableState<LoadMoreResultsTileInfo[]>(
    createLandingPageTileInfo((inventoryData ?? defaultInventory).inventory)
  );

  const [previousSearchRequestId, setPreviousSearchRequestId] = useState('');
  const [searchAnalyzeTrigger, setSearchAnalyzeTrigger] = useState(false);
  const [fetchPreviousInventoryTrigger, setFetchPreviousInventoryTrigger] =
    useState(0);
  const [isLoadingPreviousResults, setIsLoadingPreviousResults] =
    useState(false);
  const [previousResultsLoadedCount, setPreviousResultsLoadedCount] =
    useState(0);

  const loadPreviousResults = useCallback(() => {
    setIsLoadingPreviousResults(true);
    setFetchPreviousInventoryTrigger(trigger => trigger + 1);
  }, []);

  const recordPreviousResultsLoaded = useCallback(() => {
    setPreviousResultsLoadedCount(count => count + 1);
  }, []);

  const canLoadMoreResults = useMemo(() => {
    const { currentPage = 1, totalMatchedPages = 1 } =
      loadableInventoryState.data.inventory.pagination;

    return currentPage < totalMatchedPages;
  }, [loadableInventoryState.data.inventory.pagination]);

  const [fetchMoreInventoryTrigger, setFetchMoreInventoryTrigger] = useState(0);

  const loadMoreResults = useCallback(() => {
    if (!canLoadMoreResults) return;

    loadMoreResultsTileInfo.setLoading();
    setFetchMoreInventoryTrigger(trigger => trigger + 1);
  }, [canLoadMoreResults, loadMoreResultsTileInfo]);

  const updateInventoryPricing = useCallback(
    (payload: PricingResponse) => {
      loadableInventoryState.setData.call(
        null,
        prevState => ({
          ...prevState,
          inventory: {
            ...prevState.inventory,
            vehicles: prevState.inventory.vehicles.map(vehicle => {
              const {
                pricingErrorMessage = '',
                termInMonths,
                ...pricingData
              } = payload.vehiclePaymentTermsMapping[vehicle.vehicleId] ?? {
                pricingErrorMessage: 'Terms not found from pricing'
              };

              return {
                ...vehicle,
                price: vehicle.price
                  ? {
                      ...vehicle.price,
                      tradeIncentive: undefined, // these ensure we don't carry over the old incentive when switching trades
                      tradeTaxSavings: undefined,
                      ...pricingData,
                      pricingErrorMessage,
                      term: termInMonths
                    }
                  : undefined
              };
            })
          }
        }),
        { shouldSetLoaded: false }
      );
    },
    [loadableInventoryState.setData]
  );

  const resetInventoryPricing = useCallback(() => {
    loadableInventoryState.setData.call(
      null,
      prevState => ({
        ...prevState,
        inventory: {
          ...prevState.inventory,
          vehicles: prevState.inventory.vehicles.map(vehicle => ({
            ...vehicle,
            price: vehicle.price
              ? {
                  ...vehicle.price,
                  downPayment: 0,
                  monthlyPayment: 0
                }
              : undefined
          }))
        }
      }),
      { shouldSetLoaded: false }
    );
  }, [loadableInventoryState.setData]);

  const triggerFetchSearch = useCallback(() => {
    setTrigger(Date.now());
  }, []);

  const value = useMemo<ContextType>(
    () => ({
      canLoadMoreResults,
      fetchMoreInventoryTrigger,
      fetchPreviousInventoryTrigger,
      fetchSearchTrigger,
      inventory: loadableInventoryState,
      inventoryBulkData,
      isLoadingPreviousResults,
      loadMoreResults,
      loadMoreResultsTileInfo,
      loadPreviousResults,
      previousResultsLoadedCount,
      previousSearchRequestId,
      recordPreviousResultsLoaded,
      searchAnalyzeTrigger,
      similarVehicles,
      resetInventoryPricing,
      setIsLoadingPreviousResults,
      setPreviousSearchRequestId,
      setSearchAnalyzeTrigger,
      setSimilarVehicles,
      triggerFetchSearch,
      updateInventoryPricing
    }),
    [
      canLoadMoreResults,
      fetchMoreInventoryTrigger,
      fetchPreviousInventoryTrigger,
      fetchSearchTrigger,
      inventoryBulkData,
      isLoadingPreviousResults,
      loadableInventoryState,
      loadMoreResults,
      loadMoreResultsTileInfo,
      loadPreviousResults,
      previousResultsLoadedCount,
      previousSearchRequestId,
      recordPreviousResultsLoaded,
      searchAnalyzeTrigger,
      similarVehicles,
      resetInventoryPricing,
      triggerFetchSearch,
      updateInventoryPricing
    ]
  );

  return (
    <InventoryContext.Provider value={value}>
      {children}
    </InventoryContext.Provider>
  );
}

InventoryContextProvider.displayName = 'InventoryContextProvider';

export default InventoryContext;

export const generateInventoryContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(
    defaultContext,
    customState,
    { isMergeableObject: isPlainObject }
  );

export function MockedInventoryContextProvider({
  children,
  mockValue
}: React.PropsWithChildren<{ mockValue: DeepPartial<ContextType> }>) {
  return (
    <InventoryContext.Provider value={generateInventoryContext(mockValue)}>
      {children}
    </InventoryContext.Provider>
  );
}

export function useHasInventoryError(): boolean {
  const { inventory } = useContext(InventoryContext);

  return inventory.hasError;
}

export function useInventoryIsLoaded(): boolean {
  const { inventory } = useContext(InventoryContext);

  return inventory.isLoaded;
}

export function useInventoryVehicles(): InventoryVehicle[] {
  const { inventory } = useContext(InventoryContext);

  return inventory.data?.inventory?.vehicles ?? [];
}

export function usePagination(): Partial<Pagination> {
  const { inventory } = useContext(InventoryContext);

  return inventory.data?.inventory?.pagination ?? {};
}

export function useSearchRequestId(): string {
  const { inventory } = useContext(InventoryContext);

  return inventory.data?.searchRequestId ?? '';
}

export function useSimilarVehicles() {
  const { similarVehicles } = useContext(InventoryContext);

  return similarVehicles;
}

export function useUserDeliveryInfo(): UserDeliveryInfo | undefined {
  const { inventory } = useContext(InventoryContext);

  return inventory.data?.userDeliveryInfo;
}

export function useRequestIds(): {
  searchRequestId: string;
  previousSearchRequestId: string;
} {
  const { inventory, previousSearchRequestId } = useContext(InventoryContext);

  return useMemo(
    () => ({
      searchRequestId: inventory.data.searchRequestId,
      previousSearchRequestId
    }),
    [inventory.data.searchRequestId, previousSearchRequestId]
  );
}

export function useInventoryBulkData(): Record<number, AvailabilityData> {
  const { inventoryBulkData } = useContext(InventoryContext);

  return inventoryBulkData.data ?? {};
}

export function useTotalMatchedInventory(): number {
  const { inventory } = useContext(InventoryContext);

  return inventory.data?.inventory?.pagination?.totalMatchedInventory ?? 0;
}

export function useCanPaginate(): boolean {
  const { totalMatchedPages = 0 } = usePagination();

  return totalMatchedPages >= 2;
}

export function useLoadMoreResults(): {
  appendedPageCount: number;
  canLoadMoreResults: boolean;
  isLoadingMoreResults: boolean;
  loadMoreResults: () => void;
} {
  const { canLoadMoreResults, loadMoreResults, loadMoreResultsTileInfo } =
    useContext(InventoryContext);

  return {
    appendedPageCount: loadMoreResultsTileInfo.data.filter(
      page => !page.isLandingPage && !page.isLoadPrevious
    ).length,
    canLoadMoreResults,
    isLoadingMoreResults: loadMoreResultsTileInfo.isLoading,
    loadMoreResults
  };
}
